import { keepPreviousData } from '@tanstack/react-query';
import { useStanQuery } from '@/queries';
import { fetchAffiliateMetrics, fetchAffiliates } from '../analytics.logic';
import { AffiliateFiltersPayload } from '../types/affiliateAnalytics.types';
import { SENTRY_FEATURES } from '@/lib/constants';

/**
 * Query hook for fetching affiliates list with pagination and filters.
 * Uses useStanQuery with keepPreviousData to prevent UI jumps during refetches.
 */
export function useAffiliates(pageId: string, params: Omit<AffiliateFiltersPayload, 'pageSize'>) {
    return useStanQuery({
        queryKey: ['affiliates', pageId, params],
        queryFn: ({ signal }) => fetchAffiliates(pageId, { ...params, pageSize: 10 }, signal),
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes like income orders
        meta: {
            error: {
                sentryMessage: '[AFFILIATES] failed-to-fetch-affiliates',
                feature: SENTRY_FEATURES.ANALYTICS,
            },
        },
    });
}

/**
 * Query hook for fetching affiliate metrics (total earnings, sales, resellers).
 * Uses useStanQuery to show page loading state on initial load.
 */
export function useAffiliateMetrics(pageId: string) {
    return useStanQuery(
        {
            queryKey: ['affiliate-metrics', pageId],
            queryFn: ({ signal }) => fetchAffiliateMetrics(pageId, signal),
            meta: {
                error: {
                    sentryMessage: '[AFFILIATES] failed-to-fetch-metrics',
                    feature: SENTRY_FEATURES.ANALYTICS,
                },
            },
        },
        false
    );
}
